import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DicomMetadataStore, utils } from '@ohif/core';
import dicomParser from 'dicom-parser';
import filesToStudies from '../routes/Local/filesToStudies';
import helicalLogo from '../../assets/logo.png';

const LOADED_STUDIES_KEY = 'dicom-loaded-studies';

function addLoadedStudyToStorage(uid: string): void {
  try {
    const raw = localStorage.getItem(LOADED_STUDIES_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(uid)) {
      list.push(uid);
      localStorage.setItem(LOADED_STUDIES_KEY, JSON.stringify(list));
    }
  } catch { /* ignore */ }
}

const { cachedDicomFetch } = utils;

const cachedFetch = (url: string): Promise<Response> => cachedDicomFetch(url);

async function fetchInBatches<T, R>(
  items: T[],
  maxConcurrent: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      results[i] = await fn(items[i]);
    }
  }

  const workers = Array.from({ length: Math.min(maxConcurrent, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

const RemoteLocal: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [statusText, setStatusText] = useState('Loading study...');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    document.body.style.backgroundColor = 'hsl(var(--background))';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    const patientId = searchParams.get('patientId');
    const folderId = searchParams.get('folderId');
    const studyInstanceUID = searchParams.get('studyInstanceUID');

    if (!folderId || (!patientId && !studyInstanceUID)) {
      setError('Missing required query parameters: folderId and either patientId or studyInstanceUID.');
      return;
    }

    const loadStudy = async () => {
      try {
        // Step 1: Fetch the manifest
        setStatusText('Fetching study manifest...');
        const backendUrl = utils.getBackendUrl();
        let manifestUrl: string;
        if (patientId) {
          manifestUrl = `${backendUrl}/api/v1/dicom/patient/manifest.json?patientId=${patientId}&folderId=${folderId}`;
        } else if (studyInstanceUID) {
          manifestUrl = `${backendUrl}/api/v1/dicom/manifest.json?studyInstanceUID=${studyInstanceUID}&folderId=${folderId}`;
        } else {
          manifestUrl = `${backendUrl}/api/v1/dicom/patient/manifest.json?patientId=${patientId}&folderId=${folderId}`;
        }
        const response = await fetch(manifestUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch manifest: ${response.status} ${response.statusText}`);
        }

        const manifest = await response.json();

        if (!manifest.studies || manifest.studies.length === 0) {
          throw new Error('No studies found in manifest.');
        }

        // Pre-fetch metadata for all studies (first DICOM of each study)
        const studyMetadata: Record<string, any> = {};
        const metadataPromises = manifest.studies.map(async (s: any) => {
          const firstImg = s.series?.[0]?.Images?.[0];
          const firstUrl = typeof firstImg === 'string' ? firstImg : firstImg?.url;
          if (!firstUrl) return;
          try {
            const res = await cachedFetch(firstUrl);
            if (!res.ok) return;
            const arrayBuffer = await res.arrayBuffer();
            const byteArray = new Uint8Array(arrayBuffer);
            const dataSet = dicomParser.parseDicom(byteArray);

            const rawPatientName = dataSet.string('x00100010') || '';
            const patientName = rawPatientName.replace(/\^/g, ' ').trim();

            studyMetadata[s.StudyInstanceUID] = {
              PatientName: patientName,
              StudyDate: dataSet.string('x00080020') || '',
              StudyDescription: dataSet.string('x00081030') || '',
              Modality: dataSet.string('x00080060') || '',
            };
          } catch (e) {
            // skip metadata for this study
          }
        });
        await Promise.all(metadataPromises);

        // Store the full manifest globally for multi-study tab support
        (window as any).__remoteLocalManifest = {
          manifest,
          patientId,
          folderId,
          studyMetadata,
        };

        // Step 2: Determine the target study
        let firstStudy = manifest.studies[0];
        if (studyInstanceUID) {
          const target = manifest.studies.find((s: any) => s.StudyInstanceUID === studyInstanceUID);
          if (target) firstStudy = target;
        }

        // Step 3: Split each series into seeds (first 3 images) and remaining images.
        // 3 seeds per series gives OHIF enough ImagePositionPatient data to detect
        // the series as reconstructable (isReconstructable: true), so the HP selects
        // the fourUp MPR layout and tools like Crosshairs are enabled from the start.
        // Also build a url→file_id map from all manifest images for the lazy loader.
        const SEED_COUNT = 3;
        type DicomEntry = { url: string; file_id: string };
        const seedEntries: DicomEntry[] = [];
        const seriesRemainingUrls = new Map<string, string[]>(); // SeriesUID → remaining URLs
        const urlToFileIdMap = new Map<string, string>();        // url → file_id (full manifest)

        if (firstStudy.series) {
          for (const series of firstStudy.series) {
            if (!series.Images || series.Images.length === 0) continue;

            const seriesUID: string = series.SeriesInstanceUID || '';

            series.Images.forEach((img: any, idx: number) => {
              const url = typeof img === 'string' ? img : img.url;
              const fileId = typeof img === 'string' ? '' : String(img.file_id || '');

              // Track url→file_id for the lazy loader
              if (url && fileId) urlToFileIdMap.set(url, fileId);

              if (idx < SEED_COUNT) {
                // First SEED_COUNT images: download upfront
                seedEntries.push({ url, file_id: fileId });
              } else {
                // Remaining images: lazy-load after navigation
                if (seriesUID) {
                  if (!seriesRemainingUrls.has(seriesUID)) {
                    seriesRemainingUrls.set(seriesUID, []);
                  }
                  seriesRemainingUrls.get(seriesUID)!.push(url);
                }
              }
            });
          }
        }

        const nonDicomFiles: { url: string; fileName: string }[] = [];
        if (firstStudy.nonDicomInstances) {
          nonDicomFiles.push(...firstStudy.nonDicomInstances);
        }

        const totalSeedFiles = seedEntries.length + nonDicomFiles.length;
        setStatusText(`Downloading ${seedEntries.length} series previews...`);

        // Step 4: Download seed images (1 per series)
        const fileIdMap: Record<string, string> = {};
        let downloaded = 0;
        const updateProgress = () => {
          downloaded++;
          const pct = Math.round((downloaded / totalSeedFiles) * 90);
          setProgress(pct);
          setStatusText(`Downloaded ${downloaded} of ${totalSeedFiles} previews...`);
        };

        const seedFiles = await fetchInBatches(seedEntries, 6, async ({ url, file_id }) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          updateProgress();

          let fileName = 'dicom_file';
          try {
            const parsed = new URL(url);
            const filePath = parsed.searchParams.get('path');
            if (filePath) {
              fileName = filePath.split('/').pop() || fileName;
            }
          } catch { /* use default */ }

          // Parse SOPInstanceUID from seed to build initial file_id map
          if (file_id) {
            try {
              const ds = dicomParser.parseDicom(new Uint8Array(arrayBuffer));
              const sopUID = ds.string('x00080018');
              if (sopUID) {
                fileIdMap[sopUID.trim()] = file_id;
              }
            } catch { /* skip */ }
          }

          return new File([arrayBuffer], fileName, { type: 'application/dicom' });
        });

        // Step 5: Download non-DICOM files upfront (PDFs, etc.)
        const nonDicomResults = await fetchInBatches(nonDicomFiles, 6, async ({ url, fileName }) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          updateProgress();

          let mimeType = 'application/octet-stream';
          const ext = fileName.split('.').pop()?.toLowerCase();
          if (ext === 'pdf') {
            mimeType = 'application/pdf';
          } else if (ext === 'txt') {
            mimeType = 'text/plain';
          }

          return new File([arrayBuffer], fileName, { type: mimeType });
        });

        // Step 6: Combine seed + non-DICOM files
        const allSeedFiles = [...seedFiles, ...nonDicomResults].filter((f): f is File => f !== null);

        // Store non-DICOM files (PDFs, test results) so the de-identification
        // pipeline can include them when saving a teaching case.
        (window as any).__remoteLocalNonDicomFiles = nonDicomFiles
          .map((entry, i) => {
            const file = nonDicomResults[i] ?? null;
            return file ? { fileName: entry.fileName, url: entry.url, file } : null;
          })
          .filter((x): x is { fileName: string; url: string; file: File } => x !== null);

        if (allSeedFiles.length === 0) {
          throw new Error('No files could be downloaded from the manifest.');
        }

        // Step 7: Set lazy-load globals BEFORE calling filesToStudies so the
        // lazy loader is ready as soon as the viewer mounts.
        // url→file_id map lets helicalLazyLoad.ts inject file_id per batch.
        (window as any).__helicalSeriesRemainingUrls = seriesRemainingUrls;
        (window as any).__helicalFilesToStudies = filesToStudies;
        (window as any).__remoteLocalUrlToFileIdMap = urlToFileIdMap;

        // Step 8: Register seed files in OHIF pipeline
        setProgress(92);
        setStatusText('Processing previews...');
        const studies = await filesToStudies(allSeedFiles);

        // Inject file_id for seed instances into DicomMetadataStore
        if (Object.keys(fileIdMap).length > 0) {
          const studyUIDs = DicomMetadataStore.getStudyInstanceUIDs();
          for (const uid of studyUIDs) {
            const study = DicomMetadataStore.getStudy(uid);
            if (!study) continue;
            for (const ser of study.series || []) {
              for (const inst of ser.instances || []) {
                const sopUID = inst.SOPInstanceUID;
                if (sopUID && fileIdMap[sopUID]) {
                  inst.file_id = fileIdMap[sopUID];
                }
              }
            }
          }
        }

        setProgress(100);

        if (!studies || studies.length === 0) {
          throw new Error('No valid DICOM studies found in downloaded files.');
        }

        addLoadedStudyToStorage(firstStudy.StudyInstanceUID);

        // Step 9: Navigate immediately — lazy loader handles remaining images
        sessionStorage.setItem('remotelocal_patientId', patientId || '');
        sessionStorage.setItem('remotelocal_folderId', folderId || '');

        const query = new URLSearchParams();
        studies.forEach(id => query.append('StudyInstanceUIDs', id));
        query.append('datasources', 'dicomlocal');

        navigate(`/segmentation/dicomlocal?${decodeURIComponent(query.toString())}`, {
          replace: true,
        });
      } catch (err) {
        console.error('RemoteLocal error:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      }
    };

    loadStudy();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-red-400">Error</h2>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'hsl(var(--background))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={helicalLogo}
        alt="Helical"
        style={{ height: '80px', marginBottom: '40px', opacity: 0.95 }}
      />
      <div
        style={{
          width: '280px',
          height: '6px',
          backgroundColor: 'hsl(var(--card))',
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))',
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <div
        style={{
          marginTop: '12px',
          fontSize: '14px',
          fontWeight: 600,
          color: 'hsl(var(--foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {progress}%
      </div>
      <div
        style={{
          marginTop: '6px',
          fontSize: '12px',
          color: 'hsl(var(--muted-foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {statusText}
      </div>
    </div>
  );
};

export default RemoteLocal;
